import { useEffect, useRef, useState } from 'react';

interface UseExitIntentOptions {
  enabled?: boolean;
  threshold?: number;
  scrollThreshold?: number;
}

export function useExitIntent(options: UseExitIntentOptions = {}) {
  const { enabled = true, threshold = 50, scrollThreshold = 100 } = options;
  const [showExitIntent, setShowExitIntent] = useState(false);
  const mouseYRef = useRef<number>(0);
  const lastScrollTopRef = useRef<number>(0);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTriggeredRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || hasTriggeredRef.current) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const currentY = e.clientY;
      const previousY = mouseYRef.current;

      if (currentY < threshold && previousY > threshold) {
        const speed = previousY - currentY;
        if (speed > 5) {
          setShowExitIntent(true);
          hasTriggeredRef.current = true;
        }
      }

      mouseYRef.current = currentY;
    };

    const handleBlur = () => {
      if (!hasTriggeredRef.current) {
        setShowExitIntent(true);
        hasTriggeredRef.current = true;
      }
    };

    const handleScroll = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDelta = lastScrollTopRef.current - currentScrollTop;

      if (scrollDelta > scrollThreshold && currentScrollTop < 100) {
        setShowExitIntent(true);
        hasTriggeredRef.current = true;
      }

      lastScrollTopRef.current = currentScrollTop;

      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }

      scrollTimerRef.current = setTimeout(() => {
        lastScrollTopRef.current = currentScrollTop;
      }, 100);
    };

    const handleMouseEnter = (e: MouseEvent) => {
      mouseYRef.current = e.clientY;
    };

    const handleTouchStart = () => {
      lastScrollTopRef.current = window.pageYOffset || document.documentElement.scrollTop;
    };

    const handleTouchMove = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (currentScrollTop <= 10) {
        lastScrollTopRef.current = currentScrollTop;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [enabled, threshold, scrollThreshold]);

  const reset = () => {
    setShowExitIntent(false);
    hasTriggeredRef.current = false;
  };

  return { showExitIntent, reset };
}
